// client/src/components/RichContentCarousel.js
import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper";
import { Box, Stack, Typography, Skeleton } from "@mui/material";
import "swiper/css";
import "swiper/css/pagination";
import ReactPlayer from "react-player";

// Navigation button styles (copied from ItemList for consistency)
const navBtnBase = {
  position: "absolute",
  top: "35%",
  transform: "translateY(-50%)",
  zIndex: 10,
  height: 36,
  width: 36,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "2px solid rgba(255,255,255,0.95)",
  background: "rgba(0,0,0,0.35)",
  boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
  backdropFilter: "blur(2px)",
  WebkitBackdropFilter: "blur(2px)",
  color: "#fff",
  fontWeight: 800,
  lineHeight: 1,
  cursor: "pointer",
  userSelect: "none",
  WebkitTapHighlightColor: "transparent",
  transition:
    "transform .15s ease, box-shadow .15s ease, background .15s ease, border-color .15s ease",
};

const navBtnHover = {
  transform: "translateY(-50%) scale(1.06)",
  background: "rgba(0,0,0,0.5)",
  boxShadow: "0 6px 16px rgba(0,0,0,0.6)",
  borderColor: "rgba(255,255,255,1)",
};

const RichContentCarousel = ({
  content,
  index,
  onSlideChange,
  scrollToBottom,
  maxWidth = "320px",
  width = "80%",
}) => {
  const name = content.name || "";
  const photos = content.photos || [];
  const videos = content.videos || [];
  const titles = content.titles || [];
  const hasPhotos = photos.length > 0;
  const hasVideos = videos.length > 0;
  const hasMultipleSlides = photos.length > 1 || videos.length > 1;

  const swiperRef = useRef(null);
  const [hovered, setHovered] = useState({ prev: false, next: false });

  useEffect(() => {
    if (scrollToBottom) {
      scrollToBottom();
    }
  }, [scrollToBottom]);

  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const handlePrevious = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  return (
    <Box
      position="relative"
      width={width}
      maxWidth={maxWidth}
      sx={{
        margin: "0 auto",
        padding: "0 15px",
      }}
    >
      <Swiper
        style={{
          width: "100%",
          maxWidth,
          height: "auto",
          backgroundColor: "transparent",
          marginTop: "10px",
        }}
        slidesPerView={1}
        modules={[Pagination]}
        onSwiper={(swiper) => (swiperRef.current = swiper)}
        onSlideChange={onSlideChange}
        loop={hasVideos || hasMultipleSlides} // Enable loop for videos or multiple slides
      >
        {hasVideos ? (
          videos.map((video, idx) => (
            <SwiperSlide key={`video-${idx}`}>
              <Box width="100%" height="100%">
                <Box width="100%" height="100%">
                  <div style={{ fontSize: "14px", paddingBottom: "5px" }}>
                    {titles[idx] || ""}
                  </div>
                  <ReactPlayer
                    url={video}
                    width="100%"
                    height="auto"
                    loop
                    playing
                    muted
                  />
                </Box>
                <Box
                  width="100%"
                  height="100%"
                  sx={{
                    position: "absolute",
                    marginTop: "200px",
                    zIndex: 100,
                  }}
                >
                  <Stack direction="column" color="white" spacing={1.5} p={3}>
                    <Typography
                      variant="h4"
                      fontFamily="Google Sans"
                      fontSize="24px"
                      fontWeight="300"
                      color="white"
                    >
                      {name}
                    </Typography>
                    <Typography
                      variant="h7"
                      fontFamily="Google Sans"
                      fontSize="14px"
                    />
                  </Stack>
                </Box>
              </Box>
            </SwiperSlide>
          ))
        ) : hasPhotos ? (
          photos.map((photo, idx) => (
            <SwiperSlide key={`photo-${idx}`}>
              <Box width="100%" height="100%">
                {name ? (
                  <Stack
                    direction="column"
                    color="white"
                    spacing={1.5}
                    p={3}
                    sx={{ justifyContent: "center", alignItems: "center" }}
                  >
                    <Typography
                      variant="h4"
                      fontFamily="Google Sans"
                      fontSize="24px"
                      fontWeight="300"
                      color="white"
                    >
                      {name}
                    </Typography>
                  </Stack>
                ) : (
                  <></>
                )}
                <Box width="100%" height="100%">
                  <img
                    height="100%"
                    width="100%"
                    src={photo}
                    style={{
                      objectFit: "contain",
                      objectPosition: "center center",
                    }}
                    alt={`Slide ${idx + 1}`}
                  />
                </Box>
              </Box>
            </SwiperSlide>
          ))
        ) : (
          <SwiperSlide>
            <Box width="100%" height="100%">
              <Box width="100%" height="100%">
                <Skeleton height="100%" width="100%" />
              </Box>
              <Box
                width="100%"
                height="100%"
                sx={{
                  position: "absolute",
                  marginTop: `calc(${window.innerHeight}px * 0.4)`,
                  zIndex: 100,
                }}
              >
                <Stack direction="column" color="white" spacing={1.5} p={3}>
                  <Typography
                    variant="h4"
                    fontFamily="Google Sans"
                    fontSize="24px"
                    fontWeight="300"
                  >
                    {name}
                  </Typography>
                  <Typography
                    variant="h7"
                    fontFamily="Google Sans"
                    fontSize="14px"
                  />
                </Stack>
              </Box>
            </Box>
          </SwiperSlide>
        )}
      </Swiper>

      {/* Previous Button */}
      {hasMultipleSlides && (
        <button
          style={{
            ...navBtnBase,
            left: "-25px",
            ...(hovered.prev ? navBtnHover : {}),
          }}
          onMouseEnter={() => setHovered((h) => ({ ...h, prev: true }))}
          onMouseLeave={() => setHovered((h) => ({ ...h, prev: false }))}
          onClick={handlePrevious}
        >
          <span style={{ textShadow: "0 0 2px #000, 0 0 2px #fff" }}>❮</span>
        </button>
      )}

      {/* Next Button */}
      {hasMultipleSlides && (
        <button
          style={{
            ...navBtnBase,
            right: "-25px",
            ...(hovered.next ? navBtnHover : {}),
          }}
          onMouseEnter={() => setHovered((h) => ({ ...h, next: true }))}
          onMouseLeave={() => setHovered((h) => ({ ...h, next: false }))}
          onClick={handleNext}
        >
          <span style={{ textShadow: "0 0 2px #000, 0 0 2px #fff" }}>❯</span>
        </button>
      )}
    </Box>
  );
};

export default RichContentCarousel;

// Usage example:
/*
<RichContentCarousel
  content={{
    type: 'richContent',
    content: {
      type: 'carousel',
      photos: ['url1.jpg', 'url2.jpg']
    }
  }}
  index={0}
  name="Carousel Title"
  onSlideChange={(e) => console.log('Slide changed', e)}
/>
*/
